import React, { FC, useCallback, useContext, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { DriveType, ActionType } from '../../../../types';
import { useQuery } from '../../../../utils/utils';
import { getDataProperties } from '../../../../api/options';
import { EdgeDeviceTemplatePoint } from '../../index';
import { PointContext } from '../../contextTypes';
import ModbusPoint, { EdgeDeviceTemplatePoint as EdgeDeviceTemplateModbusPoint } from './modbusPoint';
import DLT645Point, { EdgeDeviceTemplatePoint as EdgeDeviceTemplateDLT645Point } from './dlt645Point';
import IEC104Point, { EdgeDeviceTemplatePoint as EdgeDeviceTemplateIEC104Point } from './IEC104Point';
import S7Point, { EdgeDeviceTemplatePoint as EdgeDeviceTemplateS7Point } from './s7Point';
import { MxColumnsType } from '@maxtropy/components';
import ThirdPoint from './ThirdPoint';

interface PointProps {
  loading?: boolean;
  fixed?: boolean;
  editColumns?: ColumnsType<EdgeDeviceTemplatePoint>;
  onUpdate?: (values: any) => any;
}

const Point: FC<PointProps> = props => {
  const { fixed, loading, onUpdate, editColumns } = props;

  const { dataSource, row, info } = useContext(PointContext);

  const driveType = info?.driveType;

  const { data: dataPropertiesAll } = useQuery(
    useCallback(
      () =>
        info?.deviceTypeId
          ? getDataProperties(info.iotProtocol, info.deviceTypeId, info.physicalModelId)
          : Promise.resolve([]),
      [info?.deviceTypeId, info?.iotProtocol, info?.physicalModelId]
    )
  );

  const usedProperties = useMemo(
    () =>
      dataSource
        ?.filter(item => item.actionType !== ActionType.DELETE && item.dataPropertyId !== row?.dataPropertyId)
        .map(item => item.dataPropertyId) || [],
    [dataSource, row]
  );

  const usedIdentifier = useMemo(
    () =>
      (dataSource
        ?.filter(item => item.actionType !== ActionType.DELETE && item.identifier !== row?.identifier)
        .map(item => item.identifier)
        .filter(Boolean) as string[]) || [],
    [dataSource, row]
  );

  const renderPoint = () => {
    if (driveType === DriveType.MODBUS_RTU || driveType === DriveType.MODBUS_TCP) {
      return (
        <ModbusPoint
          fixed={fixed}
          loading={loading}
          editColumns={editColumns as MxColumnsType<EdgeDeviceTemplateModbusPoint>}
          onUpdate={onUpdate}
          dataPropertiesAll={dataPropertiesAll}
          usedProperties={usedProperties}
          usedIdentifier={usedIdentifier}
        />
      );
    } else if (driveType === DriveType.DLT645_1997 || driveType === DriveType.DLT645_2007) {
      return (
        <DLT645Point
          loading={loading}
          editColumns={editColumns as MxColumnsType<EdgeDeviceTemplateDLT645Point>}
          onUpdate={onUpdate}
          dataPropertiesAll={dataPropertiesAll}
          usedProperties={usedProperties}
          usedIdentifier={usedIdentifier}
        />
      );
    } else if (driveType === DriveType.IEC104) {
      return (
        <IEC104Point
          loading={loading}
          editColumns={editColumns as MxColumnsType<EdgeDeviceTemplateIEC104Point>}
          onUpdate={onUpdate}
          dataPropertiesAll={dataPropertiesAll}
          usedProperties={usedProperties}
          usedIdentifier={usedIdentifier}
        />
      );
    } else if (driveType === DriveType.S7) {
      return (
        <S7Point
          fixed={fixed}
          loading={loading}
          editColumns={editColumns as MxColumnsType<EdgeDeviceTemplateS7Point>}
          onUpdate={onUpdate}
          dataPropertiesAll={dataPropertiesAll}
          usedProperties={usedProperties}
          usedIdentifier={usedIdentifier}
        />
      );
    } else if (driveType === DriveType.THIRD) {
      return (
        <ThirdPoint
          fixed={fixed}
          loading={loading}
          editColumns={editColumns as MxColumnsType<EdgeDeviceTemplateS7Point>}
          onUpdate={onUpdate}
          dataPropertiesAll={dataPropertiesAll}
          usedProperties={usedProperties}
          usedIdentifier={usedIdentifier}
        />
      );
    }
  };

  return <div>{renderPoint()}</div>;
};

export default Point;
